import { API } from "@swivl-mono/api-helper";
import { SessionInterface, UserInterface, UserVarKey, VarDefKeys, VarKey, VarType } from "@swivl-mono/types";

import { warn } from "../Loggers/SwivlLoggers";

function escapeQuotesInObject(obj: any) {
  for (const key in obj) {
    if (typeof obj[key] === "string") {
      obj[key] = obj[key].replace(/["']/g, "\\$&");
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      escapeQuotesInObject(obj[key]);
    }
  }
  return obj;
}
function escapeQuotesInString(str?: string) {
  return str?.replace(/["]/g, "\\$&");
}

export class VarsHelper {
  static varKeyForType(type: VarType, key: VarDefKeys): VarKey {
    if (type === VarType.UserVar) {
      type = VarType.User; // UserVar is a special case, we treat it as User for the key
    }
    let keyString = type + ":" + key;
    return keyString as VarKey;
  }

  static varKeysToUserVarKeys(vars: { [key: string]: any }): { [key in UserVarKey]?: string } {
    let userVars: { [key in UserVarKey]?: string } = {};
    for (const key in vars) {
      if (key.startsWith("User:")) {
        userVars[key.split(":")[1] as UserVarKey] = vars[key];
      }
    }
    return userVars;
  }

  static async replaceVariablesInJSON<T>(userOrConversation: UserInterface | { userId: number; orgId: number }, input: T): Promise<T> {
    try {
      const stringify = JSON.stringify(input);
      const replaced = await VarsHelper.replaceVariablesInString(userOrConversation, stringify);

      return JSON.parse(replaced) as T;
    } catch (e) {
      warn("Error in replaceVariablesInJSON:", input, e);
      return input;
    }
  }

  // static async replaceVariablesInJSON<T>(orgId:number, userId:number , input: T): Promise<T> {
  //     const stringify = JSON.stringify(input);
  //     const replaced = await VarsHelper.replaceVariablesInString(orgId, userId, stringify);
  //     return JSON.parse(replaced) as T;

  // }

  static async replaceVariablesInString(
    userOrConversation: UserInterface | { userId: number; orgId: number },
    input: string,
    depth: number = 0,
  ): Promise<string> {
    const userId = "userId" in userOrConversation ? userOrConversation.userId : userOrConversation.id;

    // Extract all substrings between #* and *#
    const regex = /#\*(.*?)\*#/g;
    let match;
    let substrings: string[] = [];
    const placeholders: string[] = [];

    while ((match = regex.exec(input)) !== null) {
      substrings.push(match[1]);
      placeholders.push(match[0]);
    }
    const foundVarKeys = substrings.map((s) => s.replace(/\[.*?\]/g, "")); // Here we remove any characters between square brackets

    if (substrings.length === 0) {
      return input;
    }
    const vars = await API.Vars.getVarsById(userOrConversation.orgId, userId, foundVarKeys);

    // Replace original substrings with updated values using key-value pairs
    let result = input;
    for (let index = 0; index < placeholders.length; index++) {
      const placeholder = placeholders[index];
      const value = vars[foundVarKeys[index]]?.trim().replace(/(?:\r\n|\r|\n)/g, "\\n"); // Trim and replace line breaks

      result = result.split(placeholder).join(escapeQuotesInString(value));
    }
    if (depth < 4 && VarsHelper.hasVariablesToReplace(result)) {
      return await VarsHelper.replaceVariablesInString(userOrConversation, result, depth + 1);
    }

    return result.trimEnd();
  }

  static async replaceVariablesWithVars(_input: any, vars: { [key: string]: string }, depth: number = 0): Promise<string> {
    // Extract all substrings between #* and *#
    const input = typeof _input === "string" ? _input : JSON.stringify(_input);
    const regex = /#\*(.*?)\*#/g;
    let match;
    let substrings: string[] = [];
    const placeholders: string[] = [];

    while ((match = regex.exec(input)) !== null) {
      substrings.push(match[1]);
      placeholders.push(match[0]);
    }
    const foundVarKeys = substrings.map((s) => s.replace(/\[.*?\]/g, "")); // Here we remove any characters between square brackets

    if (substrings.length === 0) {
      return input;
    }

    // Replace original substrings with updated values using key-value pairs
    let result = input;
    for (let index = 0; index < placeholders.length; index++) {
      const placeholder = placeholders[index];
      const value = vars[foundVarKeys[index]]?.trim();
      result = result.split(placeholder).join(escapeQuotesInString(value));
    }
    if (depth < 4 && VarsHelper.hasVariablesToReplace(result)) {
      return await VarsHelper.replaceVariablesWithVars(result, vars, depth + 1);
    }

    return result.trimEnd();
  }

  static async getAllVariableKeysInString(input: string): Promise<string[]> {
    // Extract all substrings between #* and *#
    const regex = /#\*(.*?)\*#/g;
    let match;
    let substrings: string[] = [];
    const placeholders: string[] = [];

    while ((match = regex.exec(input)) !== null) {
      substrings.push(match[1]);
      placeholders.push(match[0]);
    }
    const foundVarKeys = substrings.map((s) => s.replace(/\[.*?\]/g, "")); // Here we remove any characters between square brackets

    return [...new Set(foundVarKeys)]; //Make unique
  }

  static hasVariablesToReplace(input: string): boolean {
    const regex = /#\*(.*?)\*#/g;
    return regex.test(input);
  }
}
